import type { TTeamMetricId } from './types';

type TEntradaDeMetrica = {
  id: TTeamMetricId;
  token: string;
  display: 'BAR' | 'STAT';
};

/**
 * The token each editor metric travels as, and its default display.
 *
 * Copied by hand from the props table of the `create_email_template`
 * contract, section "Equipo — las métricas del bloque METRICS"
 * (`.specs/ANLT-706/contratos-backend/2026-09-08-create-email-template-mutation.md`,
 * read 2026-09-09). A hand copy goes stale when the contract changes
 * version: re-measure it against that document, never derive it from code.
 *
 * The pairing was established by the LABEL, not by list order: the eight
 * labels of `TEAM_METRICS_CATALOG` match the eight labels of the contract
 * table word for word. The contract does sanction pairing by order — its
 * catalogue "está en el orden en que el editor lista las métricas" — but an
 * index pairing breaks in silence when either list is reordered, and a name
 * pairing does not.
 *
 * `display` is the contract default. The renderer ignores it and always
 * draws a two-column card grid (catalogue contract v2, §1.8), so it changes
 * nothing visible; it travels because the item shape requires it.
 *
 * Eight entries, not ten. `TTeamMetricId` carries ten ids because
 * `VARIABLES_EQUIPO` also feeds the `{{ }}` chips of the text blocks, while
 * the switch panel — the only place a METRICS block is built from — lists
 * eight. `equipo_avance_delta` and `equipo_finalizacion_pct` are text
 * variables, not switch metrics.
 */
const TABLA_DEL_CONTRATO: readonly TEntradaDeMetrica[] = [
  {
    id: 'equipo_avance_pct',
    token: 'leader_average_team_advance',
    display: 'BAR',
  },
  {
    id: 'equipo_completados_pct',
    token: 'leader_completed_courses',
    display: 'BAR',
  },
  { id: 'equipo_activos_pct', token: 'leader_weekly_activity', display: 'BAR' },
  { id: 'equipo_meta_pct', token: 'leader_goal_completion', display: 'BAR' },
  {
    id: 'equipo_sin_iniciar_pct',
    token: 'leader_users_not_started',
    display: 'STAT',
  },
  { id: 'equipo_riesgo_pct', token: 'leader_users_at_risk', display: 'STAT' },
  {
    id: 'equipo_horas_prom',
    token: 'leader_training_hours_per_person',
    display: 'STAT',
  },
  {
    id: 'equipo_cursos_prom',
    token: 'leader_courses_per_person',
    display: 'STAT',
  },
];

/** Both directions derive from the one table, so they cannot drift apart. */
export const METRICA_POR_ID = new Map<TTeamMetricId, TEntradaDeMetrica>(
  TABLA_DEL_CONTRATO.map(entrada => [entrada.id, entrada]),
);

export const METRICA_POR_TOKEN = new Map<string, TEntradaDeMetrica>(
  TABLA_DEL_CONTRATO.map(entrada => [entrada.token, entrada]),
);

/**
 * A token the contract does not know is rejected server-side with
 * `TEMPLATE_METRICS_TOKEN_UNKNOWN`, so the editor refuses to send it.
 */
export class MetricaSinTokenDelContrato extends Error {
  constructor(metricId: string) {
    super(`Metric ${metricId} has no token in the contract table`);
    this.name = 'MetricaSinTokenDelContrato';
  }
}
